import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { doctorsApi, type DoctorProfile } from '@/api';

const DOCTOR_PROFILE_KEY = (doctorId: string) => ['doctor', doctorId, 'profile'] as const;

async function fetchDoctorProfile(doctorId: string): Promise<DoctorProfile> {
  return doctorsApi.getMyProfile(doctorId);
}

export function useDoctorProfile(doctorId: string) {
  return useQuery({
    queryKey: DOCTOR_PROFILE_KEY(doctorId),
    queryFn: () => fetchDoctorProfile(doctorId),
    enabled: !!doctorId,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    retry: 2,
  });
}

export function useUpdateDoctorProfile(doctorId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<DoctorProfile>) =>
      doctorsApi.updateMyProfile(doctorId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DOCTOR_PROFILE_KEY(doctorId) });
    },
  });
}

export function useUploadDoctorPhoto(doctorId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (photoUrl: string) =>
      doctorsApi.uploadPhoto(doctorId, photoUrl),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DOCTOR_PROFILE_KEY(doctorId) });
    },
  });
}

export function useDeleteDoctorPhoto(doctorId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => doctorsApi.deletePhoto(doctorId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DOCTOR_PROFILE_KEY(doctorId) });
    },
  });
}
